import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';

type TeacherCourseAssignmentStatus = 'Active' | 'Paused' | 'Ended';

interface TeacherCourseAssignment {
  id: number;
  organizationId: number;
  teacherUserId: number;
  teacherDisplayName: string;
  courseId: number;
  courseName: string;
  status: TeacherCourseAssignmentStatus;
  isPrimary: boolean;
  startDate: string;
  endDate?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserOption {
  userId: number;
  displayName: string;
  email: string;
}

interface CourseOption {
  id: number;
  name: string;
  pathLabel: string;
}

interface LookupResponse {
  teachers: UserOption[];
  courses: CourseOption[];
}

interface FormState {
  teacherUserId: string;
  courseId: string;
  status: TeacherCourseAssignmentStatus;
  isPrimary: boolean;
  startDate: string;
  endDate: string;
  notes: string;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const emptyForm: FormState = {
  teacherUserId: '',
  courseId: '',
  status: 'Active',
  isPrimary: true,
  startDate: '',
  endDate: '',
  notes: '',
};

const toDateInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

const mapAssignmentToForm = (assignment: TeacherCourseAssignment): FormState => ({
  teacherUserId: String(assignment.teacherUserId),
  courseId: String(assignment.courseId),
  status: assignment.status,
  isPrimary: assignment.isPrimary,
  startDate: toDateInput(assignment.startDate),
  endDate: toDateInput(assignment.endDate),
  notes: assignment.notes || '',
});

const filterControlStyle: React.CSSProperties = {
  minWidth: 180,
  height: 36,
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  color: '#1e293b',
  background: '#fff',
  boxSizing: 'border-box',
};

const ManageTeacherCourseAssignments: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<TeacherCourseAssignment[]>([]);
  const [teachers, setTeachers] = useState<UserOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');

  const requestedTeacherUserId = useMemo(() => {
    const value = Number(searchParams.get('teacherUserId'));
    return Number.isFinite(value) && value > 0 ? String(value) : '';
  }, [searchParams]);

  const selectedAssignment = useMemo(
    () => assignments.find((item) => item.id === selectedAssignmentId) || null,
    [assignments, selectedAssignmentId]
  );

  const loadData = useCallback(async () => {
    if (!hasActiveOrganization) {
      setError('No organization selected. Please switch organization first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const [assignmentResponse, lookupResponse] = await Promise.all([
        axios.get<TeacherCourseAssignment[]>(`${API_BASE_URL}/teachercourseassignments`, { headers }),
        axios.get<LookupResponse>(`${API_BASE_URL}/teachercourseassignments/lookups`, { headers }),
      ]);

      const nextAssignments = Array.isArray(assignmentResponse.data) ? assignmentResponse.data : [];
      setAssignments(nextAssignments);
      setTeachers(Array.isArray(lookupResponse.data?.teachers) ? lookupResponse.data.teachers : []);
      setCourses(Array.isArray(lookupResponse.data?.courses) ? lookupResponse.data.courses : []);

      if (mode === 'view' && !selectedAssignmentId && nextAssignments.length > 0) {
        setSelectedAssignmentId(nextAssignments[0].id);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load teacher-course assignments.');
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, selectedAssignmentId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (mode !== 'view') {
      if (!assignments.length) {
        setSelectedAssignmentId(null);
      }
      return;
    }

    if (!assignments.length) {
      setSelectedAssignmentId(null);
      setMode('view');
      return;
    }

    if (selectedAssignmentId && assignments.some((item) => item.id === selectedAssignmentId)) {
      return;
    }

    setSelectedAssignmentId(assignments[0].id);
  }, [assignments, mode, selectedAssignmentId]);

  useEffect(() => {
    setTeacherFilter(requestedTeacherUserId);
  }, [requestedTeacherUserId]);

  const filteredRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();

    return assignments.filter((item) => {
      if (search) {
        const haystack = [item.teacherDisplayName, item.courseName, item.status, item.notes || ''].join(' ').toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (statusFilter && item.status !== statusFilter) {
        return false;
      }

      if (teacherFilter && String(item.teacherUserId) !== teacherFilter) {
        return false;
      }

      return true;
    });
  }, [assignments, searchValue, statusFilter, teacherFilter]);

  useEffect(() => {
    if (!selectedAssignment || mode === 'create') {
      return;
    }

    setForm(mapAssignmentToForm(selectedAssignment));
  }, [selectedAssignment, mode]);

  const resetForm = () => {
    setForm(emptyForm);
    setError(null);
    setMessage(null);
  };

  const openCreateAssignment = () => {
    resetForm();
    setMode('create');
    setSelectedAssignmentId(null);
  };

  const openEditAssignment = (assignment: TeacherCourseAssignment) => {
    setSelectedAssignmentId(assignment.id);
    setForm(mapAssignmentToForm(assignment));
    setMode('edit');
    setError(null);
    setMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.teacherUserId || !form.courseId) {
      setError('Teacher and course are required.');
      return;
    }

    const payload = {
      teacherUserId: Number(form.teacherUserId),
      courseId: Number(form.courseId),
      status: form.status,
      isPrimary: form.isPrimary,
      startDate: form.startDate ? new Date(form.startDate).toISOString() : new Date().toISOString(),
      endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      if (mode === 'edit' && selectedAssignmentId) {
        await axios.put(`${API_BASE_URL}/teachercourseassignments/${selectedAssignmentId}`, payload, { headers });
        setMessage('Teacher-course assignment updated.');
      } else {
        await axios.post(`${API_BASE_URL}/teachercourseassignments`, payload, { headers });
        setMessage('Teacher-course assignment created.');
      }

      await loadData();
      setMode('view');
      setForm(emptyForm);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save teacher-course assignment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh' }}>
      <AdminBreadcrumbs items={[{ label: 'Admin' }, { label: 'Teacher Course Access', isCurrent: true }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#173159' }}>Teacher Course Access</h2>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Manage which teachers are assigned to which courses.</div>
        </div>
        <button
          type="button"
          onClick={openCreateAssignment}
          style={{
            background: '#173159',
            border: 'none',
            color: '#fff',
            borderRadius: 8,
            height: 36,
            padding: '0 16px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Add Teacher Course
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {message && (
        <div style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
          {message}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <input
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search teacher or course"
            style={{ ...filterControlStyle, minWidth: 220 }}
          />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={filterControlStyle}>
            <option value="">All statuses</option>
            <option value="Active">Active</option>
            <option value="Paused">Paused</option>
            <option value="Ended">Ended</option>
          </select>
          <select value={teacherFilter} onChange={(event) => setTeacherFilter(event.target.value)} style={filterControlStyle}>
            <option value="">All teachers</option>
            {teachers.map((teacher) => (
              <option key={teacher.userId} value={String(teacher.userId)}>
                {teacher.displayName}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 14, color: '#475569' }}>Loading teacher-course mappings...</div>
        ) : filteredRows.length === 0 ? (
          <div style={{ padding: 14, color: '#64748b' }}>No teacher-course mappings found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={tableHeaderStyle}>Teacher</th>
                  <th style={tableHeaderStyle}>Course</th>
                  <th style={tableHeaderStyle}>Status</th>
                  <th style={tableHeaderStyle}>Start</th>
                  <th style={tableHeaderStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => {
                      setSelectedAssignmentId(item.id);
                      setMode('view');
                    }}
                    style={{
                      cursor: 'pointer',
                      background: selectedAssignmentId === item.id ? '#eff6ff' : '#fff',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <td style={tableCellStyle}>{item.teacherDisplayName}</td>
                    <td style={tableCellStyle}>{item.courseName}</td>
                    <td style={tableCellStyle}>{item.status}</td>
                    <td style={tableCellStyle}>{formatDate(item.startDate)}</td>
                    <td style={tableCellStyle} onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => openEditAssignment(item)}
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #cbd5e1',
                          color: '#1e293b',
                          borderRadius: 8,
                          padding: '6px 10px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {mode === 'create' || mode === 'edit' ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)', padding: 20 }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, color: '#173159', fontSize: 20 }}>{mode === 'create' ? 'Add mapping' : 'Edit mapping'}</h3>
                <button type="button" onClick={() => { setMode('view'); setForm(emptyForm); }} style={{ ...secondaryButtonStyle }}>
                  Close
                </button>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                <label style={labelStyle}>
                  Teacher
                  <select value={form.teacherUserId} onChange={(event) => setForm((prev) => ({ ...prev, teacherUserId: event.target.value }))} style={fieldStyle}>
                    <option value="">Select teacher</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.userId} value={String(teacher.userId)}>
                        {teacher.displayName}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={labelStyle}>
                  Course
                  <select value={form.courseId} onChange={(event) => setForm((prev) => ({ ...prev, courseId: event.target.value }))} style={fieldStyle}>
                    <option value="">Select course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={String(course.id)}>
                        {course.pathLabel}
                      </option>
                    ))}
                  </select>
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <label style={labelStyle}>
                    Status
                    <select value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value as TeacherCourseAssignmentStatus }))} style={fieldStyle}>
                      <option value="Active">Active</option>
                      <option value="Paused">Paused</option>
                      <option value="Ended">Ended</option>
                    </select>
                  </label>

                  <label style={labelStyle}>
                    Primary mapping
                    <select value={String(form.isPrimary)} onChange={(event) => setForm((prev) => ({ ...prev, isPrimary: event.target.value === 'true' }))} style={fieldStyle}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <label style={labelStyle}>
                    Start date
                    <input type="date" value={form.startDate} onChange={(event) => setForm((prev) => ({ ...prev, startDate: event.target.value }))} style={fieldStyle} />
                  </label>

                  <label style={labelStyle}>
                    End date
                    <input type="date" value={form.endDate} onChange={(event) => setForm((prev) => ({ ...prev, endDate: event.target.value }))} style={fieldStyle} />
                  </label>
                </div>

                <label style={labelStyle}>
                  Notes
                  <textarea value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} rows={4} style={{ ...fieldStyle, resize: 'vertical', minHeight: 86 }} />
                </label>
              </div>

              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => { setMode('view'); setForm(emptyForm); }} style={{ ...secondaryButtonStyle }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : mode === 'create' ? 'Create mapping' : 'Update mapping'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const tableHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 12,
  color: '#475569',
  borderBottom: '1px solid #e2e8f0',
};

const tableCellStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  color: '#1e293b',
};

const labelStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
  color: '#334155',
  fontSize: 13,
  fontWeight: 600,
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  padding: '9px 10px',
  fontSize: 14,
  color: '#1e293b',
  background: '#fff',
  boxSizing: 'border-box',
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#173159',
  border: 'none',
  color: '#fff',
  borderRadius: 8,
  padding: '10px 16px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  background: '#f8fafc',
  border: '1px solid #cbd5e1',
  color: '#1e293b',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'grid', gap: 4, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
    <div style={{ fontSize: 12, letterSpacing: '0.03em', color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 14, color: '#0f172a', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

export default ManageTeacherCourseAssignments;
